import { z } from 'zod';

/**
 * Schadensarten. S / D / K entsprechen exakt der Legende der Papiervorlage
 * ("S = Steinschlag, D = Delle, K = Kratzer"); die uebrigen Arten decken die
 * im Formular ausformulierte Schadensdefinition ab (Glas, Felgen, Reifen, Schloesser).
 */
export const DamageKind = {
  STEINSCHLAG: 'STEINSCHLAG',
  DELLE: 'DELLE',
  KRATZER: 'KRATZER',
  GLASSCHADEN: 'GLASSCHADEN',
  RISS: 'RISS',
  FEHLTEIL: 'FEHLTEIL',
  VERSCHMUTZUNG: 'VERSCHMUTZUNG',
  SONSTIGES: 'SONSTIGES',
} as const;
export type DamageKind = (typeof DamageKind)[keyof typeof DamageKind];
export const damageKindSchema = z.nativeEnum(DamageKind);

/** Einbuchstaben-Kuerzel fuer die Marker auf dem Schema (wie auf Papier). */
export const DAMAGE_KIND_CODE: Record<DamageKind, string> = {
  STEINSCHLAG: 'S',
  DELLE: 'D',
  KRATZER: 'K',
  GLASSCHADEN: 'G',
  RISS: 'R',
  FEHLTEIL: 'F',
  VERSCHMUTZUNG: 'V',
  SONSTIGES: 'X',
};

export const DAMAGE_KIND_LABEL: Record<DamageKind, string> = {
  STEINSCHLAG: 'Steinschlag',
  DELLE: 'Delle',
  KRATZER: 'Kratzer / Schramme',
  GLASSCHADEN: 'Glasschaden (Bruch)',
  RISS: 'Riss',
  FEHLTEIL: 'Fehlteil',
  VERSCHMUTZUNG: 'Verschmutzung',
  SONSTIGES: 'Sonstiges',
};

export const DamageSeverity = { LEICHT: 'LEICHT', MITTEL: 'MITTEL', SCHWER: 'SCHWER' } as const;
export type DamageSeverity = (typeof DamageSeverity)[keyof typeof DamageSeverity];
export const damageSeveritySchema = z.nativeEnum(DamageSeverity);

export const DAMAGE_SEVERITY_LABEL: Record<DamageSeverity, string> = {
  LEICHT: 'leicht',
  MITTEL: 'mittel',
  SCHWER: 'schwer',
};

/**
 * Fahrzeugbereich. Wird vom Anwender mit einem Tipp gesetzt (nicht aus den
 * Koordinaten geraten) - dadurch bleibt die Auswertung im Backend belastbar.
 */
export const DamageArea = {
  FRONT: 'FRONT',
  HECK: 'HECK',
  SEITE_LINKS: 'SEITE_LINKS',
  SEITE_RECHTS: 'SEITE_RECHTS',
  DACH: 'DACH',
  GLAS: 'GLAS',
  RAEDER: 'RAEDER',
  INNENRAUM: 'INNENRAUM',
  LADERAUM: 'LADERAUM',
  UNTERBODEN: 'UNTERBODEN',
} as const;
export type DamageArea = (typeof DamageArea)[keyof typeof DamageArea];
export const damageAreaSchema = z.nativeEnum(DamageArea);

export const DAMAGE_AREA_LABEL: Record<DamageArea, string> = {
  FRONT: 'Front',
  HECK: 'Heck',
  SEITE_LINKS: 'Seite links (Fahrerseite)',
  SEITE_RECHTS: 'Seite rechts (Beifahrerseite)',
  DACH: 'Dach',
  GLAS: 'Glas / Scheiben',
  RAEDER: 'Räder / Felgen / Reifen',
  INNENRAUM: 'Innenraum',
  LADERAUM: 'Laderaum',
  UNTERBODEN: 'Unterboden',
};

/**
 * Leasing-Relevanz nach der Schadensdefinition im Original-Formular:
 * Kratzer/Schrammen, Dellen, Felgen- und Radkappenschaeden ab 10 mm;
 * Glas-, Reifen- und Schlossschaeden immer.
 */
export function isLeasingRelevant(kind: DamageKind, sizeMm?: number | null): boolean {
  if (kind === DamageKind.GLASSCHADEN || kind === DamageKind.RISS || kind === DamageKind.FEHLTEIL) {
    return true;
  }
  if (kind === DamageKind.VERSCHMUTZUNG) return false;
  if (sizeMm == null) return true; // im Zweifel dokumentieren
  return sizeMm >= 10;
}

export const damageMarkerSchema = z.object({
  clientId: z.string().min(1).max(64),
  schematicId: z.string().min(1),
  /** Normalisierte Koordinaten 0..1 relativ zum Schema-Bild. */
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  kind: damageKindSchema,
  severity: damageSeveritySchema.default('LEICHT'),
  area: damageAreaSchema.optional(),
  sizeMm: z.number().int().min(0).max(5000).optional().nullable(),
  note: z.string().max(1000).optional(),
  /** Vorschaden - war bereits bei Uebernahme vorhanden. */
  preexisting: z.boolean().default(false),
  /** clientIds der zugehoerigen Fotos. */
  photoClientIds: z.array(z.string()).default([]),
});
export type DamageMarkerInput = z.infer<typeof damageMarkerSchema>;
